import { useState } from "react";
import type { Movie } from "@shared/types";
import { ApiError, createMovie, getMovieDetails, searchMovies } from "../api";
import { MovieConfirm } from "./MovieConfirm";
import { SafeCover } from "./SafeCover";

export function ScanMovieTab() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Movie[] | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [details, setDetails] = useState<Movie | null>(null);
  const [savedTitle, setSavedTitle] = useState<string | null>(null);

  const onSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    setPending(true);
    setError(null);
    try {
      const r = await searchMovies(query.trim());
      setResults(r.results);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Search failed");
    } finally {
      setPending(false);
    }
  };

  const pickResult = async (m: Movie) => {
    if (!m.tmdb_id) return;
    setPending(true);
    setError(null);
    try {
      const r = await getMovieDetails(
        m.tmdb_id,
        (m.media_type === "series" ? "series" : "movie"),
      );
      setDetails(r.movie);
      setResults(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Lookup failed");
    } finally {
      setPending(false);
    }
  };

  const onSave = async (movie: Movie) => {
    try {
      const saved = await createMovie(movie);
      setDetails(null);
      setQuery("");
      setSavedTitle(saved.title);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Save failed");
    }
  };

  if (details) {
    return (
      <MovieConfirm
        movie={details}
        onSave={onSave}
        onCancel={() => setDetails(null)}
      />
    );
  }

  return (
    <div className="space-y-4">
      <form onSubmit={onSearch} className="space-y-2">
        <label className="block text-sm" htmlFor="movie-q">
          Search title
        </label>
        <div className="flex gap-2">
          <input
            id="movie-q"
            type="text"
            value={query}
            autoFocus
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Inception, Breaking Bad…"
            className="flex-1 px-4 py-3 text-lg bg-gray-800 border border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="submit"
            className="px-4 py-3 bg-blue-600 hover:bg-blue-500 rounded font-medium"
          >
            Search
          </button>
        </div>
      </form>
      {pending && <p className="text-sm text-gray-400">Searching…</p>}
      {error && (
        <div role="alert" className="text-sm text-red-400">
          {error}
        </div>
      )}
      {savedTitle && <div className="text-sm text-green-400">Saved: {savedTitle}</div>}
      {results && (
        <ul className="space-y-2">
          {results.length === 0 && (
            <li className="text-sm text-gray-400">No results.</li>
          )}
          {results.map((m) => (
            <li key={m.tmdb_id ?? m.title}>
              <button
                type="button"
                onClick={() => pickResult(m)}
                className="w-full flex gap-3 p-2 bg-gray-800 hover:bg-gray-700 rounded text-left"
              >
                <SafeCover url={m.cover_url} alt={m.title} className="w-12 h-16 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{m.title}</div>
                  <div className="text-xs text-gray-400">
                    {m.media_type === "series" ? "Series" : "Movie"}
                    {m.year ? ` • ${m.year}` : ""}
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
